/**
 * Initiatives Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { initiativesApi, kpisApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';
import { InitiativeList, Initiative } from '../../types';

interface InitiativesListParams {
  page?: number;
  pageSize?: number;
  goal?: number | null;
  organization?: number;
  status?: string;
  year?: number;
}

interface InitiativesListResponse {
  results: InitiativeList[];
  count: number;
}

export function useInitiativesQuery(params?: InitiativesListParams) {
  return useQuery({
    queryKey: queryKeys.initiatives.list(params),
    queryFn: async (): Promise<InitiativesListResponse> => {
      const queryParams: Record<string, any> = {};
      if (params?.page) queryParams.page = params.page;
      if (params?.pageSize) queryParams.page_size = params.pageSize;
      if (params?.goal) queryParams.strategic_goal = params.goal;
      if (params?.organization) queryParams.organization = params.organization;
      if (params?.status) queryParams.approval_status = params.status;
      if (params?.year) queryParams.year = params.year;

      return await initiativesApi.list(queryParams);
    },
    staleTime: STALE_TIMES.INITIATIVES,
  });
}

export function useInitiativeDetailQuery(initiativeId: number) {
  return useQuery({
    queryKey: queryKeys.initiatives.detail(initiativeId),
    queryFn: () => initiativesApi.get(initiativeId),
    enabled: !!initiativeId,
    staleTime: STALE_TIMES.INITIATIVES,
  });
}

export function useInitiativeKPIsQuery(initiativeId: number) {
  return useQuery({
    queryKey: queryKeys.initiatives.kpis(initiativeId),
    queryFn: () => kpisApi.list({ initiative: initiativeId }),
    enabled: !!initiativeId,
    staleTime: STALE_TIMES.KPIS,
  });
}

export function useDeleteInitiativeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (initiativeId: number) => initiativesApi.delete(initiativeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
    },
  });
}

export function useCreateInitiativeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Initiative>) => initiativesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.all });
    },
  });
}

export function useUpdateInitiativeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Initiative> }) =>
      initiativesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.initiatives.detail(variables.id) });
    },
  });
}

export default useInitiativesQuery;
